// src/utils/exportChartSvg.js
// Exportiert ein gerendertes Recharts-Diagramm als eigenständige SVG-Datei.
//
// Recharts zeichnet Achsen/Balken/Segmente in ein <svg class="recharts-surface">,
// die Legende aber als HTML (<ul class="recharts-legend-wrapper">) darüber. Für
// eine vollständige Datei wird das SVG geklont und die Legende an ihrer
// sichtbaren Position als SVG-Elemente (Icon + Text) nachgezeichnet. Dazu ein
// Hintergrund-Rechteck in der aktuellen Seitenfarbe, damit die Achsenbeschriftung
// (Theme-Textfarbe) auch im Dark-Mode lesbar bleibt.

const SVG_NS = "http://www.w3.org/2000/svg";

const isTransparent = (c) => !c || c === "transparent" || /rgba\(.*,\s*0\)$/.test(c);

// Erste nicht-transparente Hintergrundfarbe vom Element aufwärts.
const effectiveBackground = (el) => {
  for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
    const bg = getComputedStyle(n).backgroundColor;
    if (!isTransparent(bg)) return bg;
  }
  return "#ffffff";
};

const safeFilename = (s) =>
  String(s || "")
    .replace(/[\\/:*?"<>|\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);

/**
 * @param {HTMLElement} container Element, das das Recharts-Diagramm enthält
 * @param {string} [name] Basis für den Dateinamen (z. B. Fragetext)
 * @returns {boolean} false, wenn kein Diagramm gefunden wurde
 */
export const exportChartSvg = (container, name) => {
  const surface = container?.querySelector("svg.recharts-surface");
  if (!surface) return false;

  const surfaceRect = surface.getBoundingClientRect();
  const width = Math.round(surfaceRect.width);
  const height = Math.round(surfaceRect.height);
  const rootStyle = getComputedStyle(surface);

  const svg = surface.cloneNode(true);
  svg.setAttribute("xmlns", SVG_NS);
  svg.setAttribute("width", width);
  svg.setAttribute("height", height);
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  svg.setAttribute("font-family", rootStyle.fontFamily);
  svg.removeAttribute("class");
  svg.removeAttribute("style");
  svg.querySelectorAll(".recharts-tooltip-cursor, .recharts-active-dot").forEach((n) => n.remove());

  const bg = document.createElementNS(SVG_NS, "rect");
  bg.setAttribute("width", width);
  bg.setAttribute("height", height);
  bg.setAttribute("fill", effectiveBackground(surface.parentElement));
  svg.insertBefore(bg, svg.firstChild);

  // Legende (HTML) als SVG nachzeichnen, relativ zur Diagrammfläche positioniert.
  const legend = document.createElementNS(SVG_NS, "g");
  legend.setAttribute("class", "legend");
  container.querySelectorAll(".recharts-legend-item").forEach((item) => {
    const icon = item.querySelector("svg");
    if (icon) {
      const r = icon.getBoundingClientRect();
      const iconClone = icon.cloneNode(true);
      iconClone.setAttribute("x", r.left - surfaceRect.left);
      iconClone.setAttribute("y", r.top - surfaceRect.top);
      iconClone.setAttribute("width", r.width);
      iconClone.setAttribute("height", r.height);
      iconClone.removeAttribute("style");
      iconClone.removeAttribute("class");
      legend.appendChild(iconClone);
    }
    const label = item.querySelector(".recharts-legend-item-text");
    if (label) {
      const r = label.getBoundingClientRect();
      const ls = getComputedStyle(label);
      const text = document.createElementNS(SVG_NS, "text");
      text.setAttribute("x", r.left - surfaceRect.left);
      text.setAttribute("y", r.top - surfaceRect.top + r.height / 2);
      text.setAttribute("dominant-baseline", "central");
      text.setAttribute("fill", ls.color);
      text.setAttribute("font-size", ls.fontSize);
      text.textContent = label.textContent;
      legend.appendChild(text);
    }
  });
  if (legend.childNodes.length) svg.appendChild(legend);

  const markup = `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(svg)}`;
  const blob = new Blob([markup], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeFilename(name) || "Diagramm"}_${new Date().toISOString().slice(0, 10)}.svg`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
};
